import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaHotel, FaPlane, FaSuitcaseRolling, FaShip } from "react-icons/fa";
import { FiExternalLink as ExternalLinkIcon } from "react-icons/fi";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { resolveApiUrl } from "../../../assets/GlobalVariables";
import { performPostFetch } from "../../../services/fetchService";
import {
  RECENT_ORDERS_HOME_FEED_ENDPOINT,
  buildRecentOrdersHomeFeedHeaders,
  canOpenRecentFeedOrderDetail,
  parseRecentOrdersHomeFeedResponse,
  resolveHomeFeedSessionId,
  resolveHomeFeedViewerIsAdmin,
  traceRecentOrdersHomeFeed,
} from "../../../utils/recentOrdersHomeFeed";
import { buildOrdersDetailNavigationState } from "../../../features/orders/filters";
import { resolveRecentOrderDashboardDetailPath } from "../../../utils/ordersDashboardRoutes";
import {
  resolveOrderTitleForDisplay,
  resolveRecentOrderListIconKind,
  resolveOrderProductDisplay,
  shouldShowRecentOrderLabeledDates,
} from "../../../utils/orders/resolveOrderProductDisplay";
import { resolveOrderSourceBadge } from "../../../utils/orders/orderSourceBadge";
import { resolveCampaignHotelImageUrl } from "../../../utils/orders/resolveCampaignHotelImage";
import { resolveCampaignOrderCommercialBreakdown } from "../../../utils/orders/resolveCampaignOrderFinancialSsot";
import {
  resolveCampaignHotelContext,
  resolveCampaignOrderHotelDisplayName,
} from "../../../utils/orders/campaignOrderContext";
import placeholderImage from "../../../assets/images/icons/hotel.png";
import cruiseThumbnail from "../../../assets/images/backgrounds/Crown_Iris1_.jpg";
import "./RecentOrdersCard.scss";

dayjs.extend(customParseFormat);

const parseDate = (value) => {
  if (!value) return null;
  const dayFirst = dayjs(value, "DD/MM/YYYY", true);
  if (dayFirst.isValid()) {
    return dayFirst;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const formatDate = (value) => {
  const parsed = parseDate(value);
  return parsed ? parsed.format("DD/MM/YYYY") : "—";
};

const getDisplayTitle = (order) => {
  const orderType = String(order?.type || "").toLowerCase();
  if (orderType === "cruise") {
    return "מנו ספנות";
  }

  return resolveOrderTitleForDisplay(order);
};

/** Total agent entitlement (built-in public + added) from campaign pricing. */
const resolveCampaignAgentCommissionIls = (order, campaign) => {
  const commercial = resolveCampaignOrderCommercialBreakdown(order, { isAdmin: true });
  if (commercial && commercial.agentAmount > 0) return commercial.agentAmount;
  if (!campaign) return null;
  const pricing =
    campaign.pricing && typeof campaign.pricing === "object" ? campaign.pricing : null;
  const raw =
    campaign.agent_added_commission_ils ??
    campaign.agentAddedCommissionIls ??
    pricing?.agent_added_commission_ils ??
    pricing?.agentAddedCommissionIls ??
    pricing?.flight_agent_commission_ils;
  if (raw == null || String(raw).trim() === "") return null;
  const commission = Number(raw);
  return Number.isFinite(commission) ? commission : null;
};

const hasCampaignNetCommission = (order, campaign) => {
  if (!campaign) return false;
  const entitled = resolveCampaignAgentCommissionIls(order, campaign);
  if (entitled != null && entitled > 0.01) return false;
  if (campaign.is_net === true || campaign.isNet === true) return true;
  return entitled != null && Math.abs(entitled) < 0.01;
};

const RecentOrdersCard = ({
  maxItems = 15,
  useDashboardOrders = false,
  ordersFromDashboard = null,
  dashboardStatsLoading = false,
  feedErrorType: feedErrorTypeProp = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorType, setErrorType] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    if (useDashboardOrders) return;
    let isMounted = true;
    const headers = buildRecentOrdersHomeFeedHeaders();
    const isAdmin = resolveHomeFeedViewerIsAdmin();

    setIsLoading(true);
    setErrorType(null);
    const sessionId = resolveHomeFeedSessionId();
    const payload = { limit: maxItems };
    if (sessionId) {
      payload.sessionID = sessionId;
    }
    (async () => {
      try {
        const res = await performPostFetch(
          resolveApiUrl(RECENT_ORDERS_HOME_FEED_ENDPOINT),
          payload,
          false,
          { headers }
        );
        if (!isMounted) return;
        if (res == null) {
          setErrorType("error");
          setOrders([]);
          traceRecentOrdersHomeFeed({
            role: null,
            endpoint: RECENT_ORDERS_HOME_FEED_ENDPOINT,
            rowsCount: 0,
            filteredCount: 0,
            isAdmin,
            feedOk: false,
            feedError: "null_response",
          });
          return;
        }
        const parsed = parseRecentOrdersHomeFeedResponse(res);
        const data = parsed.orders;
        traceRecentOrdersHomeFeed({
          role: null,
          endpoint: RECENT_ORDERS_HOME_FEED_ENDPOINT,
          rowsCount: data.length,
          filteredCount: data.length,
          isAdmin,
          httpStatus: res?.status ?? null,
          feedOk: parsed.ok,
          feedError: parsed.error,
        });
        if (!parsed.ok) {
          setErrorType(parsed.forbidden ? "forbidden" : "error");
          setOrders([]);
          return;
        }
        setOrders(data);
        setLastUpdatedAt(Date.now());
      } catch (error) {
        if (!isMounted) return;
        const status = error?.response?.status;
        if (status === 403) {
          setErrorType("forbidden");
        } else {
          setErrorType("error");
        }
        setOrders([]);
        traceRecentOrdersHomeFeed({
          role: null,
          endpoint: RECENT_ORDERS_HOME_FEED_ENDPOINT,
          rowsCount: 0,
          filteredCount: 0,
          isAdmin,
          fetchException: error?.message ?? String(error),
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [maxItems, useDashboardOrders]);

  useEffect(() => {
    if (!useDashboardOrders) return;
    if (Array.isArray(ordersFromDashboard)) {
      setLastUpdatedAt(Date.now());
    }
  }, [useDashboardOrders, ordersFromDashboard]);

  const listOrders = useDashboardOrders
    ? Array.isArray(ordersFromDashboard)
      ? ordersFromDashboard
      : []
    : orders;
  const listLoading = useDashboardOrders
    ? dashboardStatsLoading || ordersFromDashboard === null
    : isLoading;
  const effectiveErrorType = useDashboardOrders ? feedErrorTypeProp : errorType;
  const canOpenOrderDetail = canOpenRecentFeedOrderDetail();

  const visibleOrders = useMemo(() => {
    const withTs = listOrders.map((order, index) => ({
      order,
      index,
      ts: parseDate(order?.eventAt)?.valueOf() ?? 0,
      id: String(order?.id ?? ""),
    }));
    withTs.sort((a, b) => {
      if (a.ts !== b.ts) return b.ts - a.ts;
      if (a.id !== b.id) return b.id.localeCompare(a.id);
      return a.index - b.index;
    });
    return withTs.slice(0, maxItems).map((item) => item.order);
  }, [listOrders, maxItems]);

  const updatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return null;
    const diffSeconds = Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000));
    if (diffSeconds < 60) return `עודכן לפני ${diffSeconds} שניות`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `עודכן לפני ${diffMinutes} דקות`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `עודכן לפני ${diffHours} שעות`;
  }, [lastUpdatedAt]);

  const handleOrderClick = (order) => {
    if (!canOpenOrderDetail || !order?.id) return;
    navigate(resolveRecentOrderDashboardDetailPath(order), {
      state: buildOrdersDetailNavigationState(order, location),
    });
  };

  return (
    <div className="recent-orders-card" dir="rtl">
      <div className="recent-orders-card__header">
        <div className="recent-orders-card__header-main">
          <h2 className="recent-orders-card__title">הזמנות אחרונות במערכת</h2>
          {updatedLabel && (
            <span className="recent-orders-card__badge">{updatedLabel}</span>
          )}
        </div>
        <button
          type="button"
          className="recent-orders-card__view-all"
          onClick={() => navigate("/p/dashboard/orders")}
        >
          כל ההזמנות
          <ExternalLinkIcon />
        </button>
      </div>

      <div className="recent-orders-card__body">
        {listLoading ? (
          <div className="recent-orders-card__loading">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="recent-orders-card__skeleton-row" />
            ))}
          </div>
        ) : effectiveErrorType === "forbidden" ? (
          <div className="recent-orders-card__empty">
            אין הרשאה לפיד מערכת
          </div>
        ) : effectiveErrorType === "error" ? (
          <div className="recent-orders-card__empty">
            <div>טעינה נכשלה</div>
            <button
              type="button"
              className="recent-orders-card__retry"
              onClick={() => window.location.reload()}
            >
              נסה שוב
            </button>
          </div>
        ) : visibleOrders.length === 0 ? (
          <div className="recent-orders-card__empty">אין הזמנות אחרונות</div>
        ) : (
          <div className="recent-orders-card__list">
            {visibleOrders.map((order, index) => {
              const product = resolveOrderProductDisplay(order);
              const campaignHotel = resolveCampaignHotelContext(order);
              const campaignHotelName = resolveCampaignOrderHotelDisplayName(order);
              const orderSourceBadge = resolveOrderSourceBadge(order);
              const shouldHidePrice =
                Boolean(campaignHotel) ||
                orderSourceBadge?.key === "campaign" ||
                orderSourceBadge?.key === "corporate";
              const iconKind = campaignHotelName
                ? "hotel"
                : resolveRecentOrderListIconKind(order);
              const typeIcon =
                iconKind === "hotel" ? (
                  <FaHotel />
                ) : iconKind === "cruise" ? (
                  <FaShip />
                ) : iconKind === "flight" ? (
                  <FaPlane />
                ) : iconKind === "organized_tour" ? (
                  <FaSuitcaseRolling />
                ) : (
                  <FaSuitcaseRolling />
                );
              const displayTitle =
                campaignHotelName ||
                product.title ||
                getDisplayTitle(order);
              const displaySubtitle =
                product.subtitle ||
                (campaignHotelName ? product.routeLabel || "" : "");
              const useHotelPlaceholder = iconKind === "hotel" || iconKind === "package";
              const thumbnailUrl =
                iconKind === "cruise"
                  ? cruiseThumbnail
                  : resolveCampaignHotelImageUrl(order) ||
                    campaignHotel?.hotel_image_url ||
                    campaignHotel?.hotelImageUrl ||
                    product.imageUrl ||
                    order?.thumbnailUrl ||
                    order?.thumbnail_url ||
                    (useHotelPlaceholder ? placeholderImage : null);
              const dateStartLabel = product.isOrganizedTourPresentation ? "יציאה:" : "צ'ק-אין:";
              const dateEndLabel = product.isOrganizedTourPresentation ? "חזרה:" : "צ'ק-אאוט:";
              const checkInValue = product.departureDate || order?.checkIn;
              const checkOutValue = product.returnDate || order?.checkOut;
              const displayTotal = Number(order?.displayTotalILS);
              const hasDisplayTotal = Number.isFinite(displayTotal) && displayTotal > 0;
              const feeValue =
                order?.handlingFeeILS != null ? Number(order.handlingFeeILS) : null;
              const feeLabel =
                hasDisplayTotal
                  ? String(order?.displayTotalCurrency || "ILS").toUpperCase() === "ILS"
                    ? `₪${displayTotal.toFixed(0)}`
                    : `${String(order?.displayTotalCurrency).toUpperCase()} ${displayTotal.toFixed(0)}`
                  : feeValue == null
                    ? "—"
                    : feeValue === 0
                      ? "NET"
                      : `₪${feeValue.toFixed(0)}`;
              const showNetBadge = hasCampaignNetCommission(order, campaignHotel);
              // Campaign rows hide handlingFeeILS / customer total; show agent entitlement instead.
              const campaignCommissionIls = resolveCampaignAgentCommissionIls(order, campaignHotel);
              const showCampaignCommissionBadge =
                Boolean(campaignHotel) &&
                !showNetBadge &&
                campaignCommissionIls != null &&
                campaignCommissionIls > 0;

              return (
                <div
                  key={order?.id || index}
                  className={`recent-orders-card__item${
                    canOpenOrderDetail ? "" : " recent-orders-card__item--read-only"
                  }`}
                  onClick={() => handleOrderClick(order)}
                  role={canOpenOrderDetail ? "button" : undefined}
                  tabIndex={canOpenOrderDetail ? 0 : undefined}
                >
                  <div className="recent-orders-card__item-thumb">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={displayTitle}
                        loading="lazy"
                        onError={(e) => {
                          if (useHotelPlaceholder) {
                            e.currentTarget.src = placeholderImage;
                          } else {
                            e.currentTarget.style.display = "none";
                          }
                        }}
                      />
                    ) : (
                      <span className="recent-orders-card__item-thumb-fallback" aria-hidden>
                        {typeIcon}
                      </span>
                    )}
                  </div>
                  <div className="recent-orders-card__item-content">
                    <div className="recent-orders-card__item-header">
                      <div className="recent-orders-card__item-type">{typeIcon}</div>
                      <div>
                        <h3 className="recent-orders-card__item-title">{displayTitle}</h3>
                        {displaySubtitle ? (
                          <p className="recent-orders-card__item-subtitle">{displaySubtitle}</p>
                        ) : null}
                      </div>
                    </div>
                    {shouldShowRecentOrderLabeledDates(product) ? (
                      <div className="recent-orders-card__item-dates">
                        <span className="recent-orders-card__item-date">
                          <span className="recent-orders-card__item-date-label">{dateStartLabel}</span>
                          <span className="recent-orders-card__item-date-value">
                            {formatDate(checkInValue)}
                          </span>
                        </span>
                        <span className="recent-orders-card__item-date">
                          <span className="recent-orders-card__item-date-label">{dateEndLabel}</span>
                          <span className="recent-orders-card__item-date-value">
                            {formatDate(checkOutValue)}
                          </span>
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="recent-orders-card__item-footer">
                    {showNetBadge ? (
                      <span className="recent-orders-card__item-net-badge">NET</span>
                    ) : null}
                    {showCampaignCommissionBadge ? (
                      <span className="recent-orders-card__item-fee">
                        {`₪${campaignCommissionIls.toFixed(0)}`}
                      </span>
                    ) : null}
                    {!shouldHidePrice ? (
                      <span className="recent-orders-card__item-fee">{feeLabel}</span>
                    ) : null}
                    <ExternalLinkIcon className="recent-orders-card__item-action" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentOrdersCard;
